import * as React from "react";
import { Select, theme } from "antd";
import { Field } from "src/search/types";
import { asKey } from "src/utils/fields";
import {
  entityAttributeDefinition,
  entityDefinition,
} from "@vzbl/integrations";
import { CustomTooltip } from "src/components/CustomTooltip";

interface FieldSelectorProps {
  value?: Field;
  onChange: (field: Field | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  relevantFields: Field[];
}

export function FieldSelector({
  value,
  onChange,
  placeholder = "Search for a field...",
  disabled = false,
  relevantFields,
  defaultOpen = false,
}: FieldSelectorProps) {
  const { token } = theme.useToken();

  const filters = relevantFields;

  // Format field label for display only
  const formatFieldLabel = (field: Field) => {
    // Remove 'vzbl' prefix and format the rest
    const attribute = field.attribute.replace(/^vzbl/, "");
    // Convert camelCase to Title Case
    const formattedAttribute = attribute
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return {
      text: formattedAttribute,
      isDerived: field.attribute.startsWith("vzbl"),
    };
  };

  // Format target name for better readability
  const formatTargetName = (target: string) => {
    return target
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Enhanced search function that handles partial and out-of-order matches
  const searchField = React.useCallback((input: string, option: any) => {
    if (!input.trim()) return true;

    // If it's a group, search in the group label
    if ("options" in option) {
      return option.label.toLowerCase().includes(input.toLowerCase());
    }

    // For field options, create a searchable text from the stored searchText
    const searchTerms = input.toLowerCase().split(/\s+/);
    return searchTerms.every((term) =>
      option.searchText.toLowerCase().includes(term)
    );
  }, []);

  // Group fields by target with formatted labels
  const fieldGroups = React.useMemo(() => {
    const groups = new Map<string, Field[]>();
    filters.forEach((field) => {
      const group = groups.get(field.target) || [];
      group.push(field);
      groups.set(field.target, group);
    });

    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([target, fields]) => {
        const targetLabel = formatTargetName(target);

        return {
          label: targetLabel,
          options: fields
            .sort((a, b) => a.attribute.localeCompare(b.attribute))
            .map((field) => {
              const { text, isDerived } = formatFieldLabel(field);
              const entity = entityDefinition(field.target);
              const description = entityAttributeDefinition(asKey(field));
              const tooltipText =
                entity && description ? (
                  <>
                    <div>
                      <b>{field.target}</b>: {entity?.description}
                    </div>
                    <div>
                      <b>{field.attribute}</b>: {description}
                    </div>
                  </>
                ) : undefined;
              return {
                ...field, // Keep the original field data
                value: asKey(field),
                // Store a plain text version for searching
                searchText: `${targetLabel} ${text}`,
                // Use render function for the label display
                label: (
                  <MaybeTooltip content={tooltipText}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>{text}</span>
                      {isDerived && (
                        <span
                          style={{
                            fontSize: 12,
                            color: token.colorTextDescription,
                            marginLeft: 4,
                          }}
                        >
                          (Derived)
                        </span>
                      )}
                    </div>
                  </MaybeTooltip>
                ),
                description: targetLabel,
              };
            }),
        };
      });
  }, [filters, token.colorTextDescription]);

  // Handle field selection
  const handleFieldChange = (key: string) => {
    const selectedField = fieldGroups
      .flatMap((group) => group.options)
      .find((opt) => opt.value === key);

    // Only pass the original Field properties back
    if (selectedField) {
      const { target, attribute } = selectedField;
      onChange({ target, attribute });
    } else {
      onChange(undefined);
    }
  };

  return (
    <Select
      style={{ width: "100%" }}
      showSearch
      placeholder={placeholder}
      value={value ? asKey(value) : undefined}
      onChange={handleFieldChange}
      options={fieldGroups}
      filterOption={searchField}
      optionFilterProp="searchText"
      dropdownRender={(menu) => menu}
      disabled={disabled}
      defaultOpen={defaultOpen}
      listHeight={256}
      virtual={true}
      popupMatchSelectWidth
    />
  );
}

const MaybeTooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: React.ReactNode;
}) => {
  return content ? (
    <CustomTooltip content={content} placement="right">
      {children}
    </CustomTooltip>
  ) : (
    <>{children}</>
  );
};
